import { PERMISSIONS } from '@ibe/constants';
import { Button, StatusBadge } from '@ibe/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { EntitySelect } from '../../../components/form/entity-select.js';
import { ImportDialog } from '../../../components/import-dialog/import-dialog.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { PermissionGuard } from '../../../auth/permission-guard.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { cityRowSchema } from '../api/schemas.js';

/**
 * The cities screen.
 *
 * Two cascaded filters above the table: country, then state. A state that does
 * not belong to the chosen country is a pair the API will refuse, so changing
 * the country clears the state — leaving the old one selected sends an
 * impossible pair and shows an empty table that looks like there are no cities.
 */
export function CityListPage(): React.JSX.Element {
  const [importing, setImporting] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');

  const chooseCountry = (value: string): void => {
    setCountryCode(value);
    setStateCode('');
  };

  const scope: Record<string, string> = {};
  if (countryCode !== '') scope.countryCode = countryCode;
  if (stateCode !== '') scope.stateCode = stateCode;

  return (
    <>
      <div className="mb-4 flex items-end gap-4">
        <EntitySelect
          path="/v1/countries/options"
          queryKey={queryKeys.reference.countries}
          value={countryCode}
          onChange={chooseCountry}
          label="Country"
          placeholder="Select a country"
        />
        <EntitySelect
          path="/v1/states/options"
          queryKey={queryKeys.reference.states(countryCode)}
          value={stateCode}
          onChange={setStateCode}
          label="State"
          placeholder="Select a state"
          params={countryCode === '' ? {} : { countryCode }}
          enabled={countryCode !== ''}
          disabledHint="Choose a country first"
        />
      </div>

      <ResourceListPage
        title="Cities"
        description="Cities and towns. Filter by country and state, search by name."
        path="/v1/cities"
        resource="cities"
        schema={cityRowSchema}
        permission={PERMISSIONS.REFERENCE_READ}
        rowKey={(row) => row.id}
        defaultSort={{ columnId: 'name', direction: 'asc' }}
        scope={scope}
        actions={
          <PermissionGuard permission={PERMISSIONS.REFERENCE_IMPORT}>
            <Button
              onClick={() => {
                setImporting(true);
              }}
            >
              Import
            </Button>
          </PermissionGuard>
        }
        columns={[
          {
            id: 'name',
            header: 'Name',
            sortable: true,
            fields: ['name'],
            cell: (row) => (
              <Link
                className="font-medium underline-offset-4 hover:underline"
                to={`/cities/${row.id}`}
              >
                {row.name}
              </Link>
            ),
          },
          { id: 'code', header: 'Code', fields: ['code'], cell: (row) => row.code ?? '—' },
          {
            id: 'stateName',
            header: 'State',
            fields: ['stateName'],
            cell: (row) => row.stateName ?? '—',
          },
          {
            id: 'countryCode',
            header: 'Country',
            fields: ['countryCode'],
            cell: (row) => row.countryCode ?? '—',
          },
          {
            id: 'isActive',
            header: 'Status',
            fields: ['isActive'],
            cell: (row) => (
              <StatusBadge tone={row.isActive === true ? 'success' : 'neutral'}>
                {row.isActive === true ? 'Active' : 'Inactive'}
              </StatusBadge>
            ),
          },
        ]}
      />

      <ImportDialog
        resource="cities"
        basePath="/v1/cities"
        open={importing}
        onClose={() => {
          setImporting(false);
        }}
      />
    </>
  );
}
